import * as React from 'react';
import { CSSProperties } from 'react';
import { compose, setDisplayName, withHandlers, withState } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin } from 'ui/styles/skin';
import { DeviceMap, DeviceIndex, DeviceIdMap, UtcTime } from 'types';
import { State } from 'reducers';
import { getDevices, isLoadingDevices, getDeviceIdByDeviceIndex } from 'selectors/deviceSelectors';
import { Button } from 'ui/shared/Button';
import { assignStream, setScroll } from 'actions/monitoringActions';
import { ButtonWithSpinner } from 'ui/shared/ButtonWithSpinner';
import { postSeekRequest } from 'api/camera';
import { ChangeImageCell } from './ChangeImageCell';
import { HasCellIndex, getStreamPosition } from 'selectors/monitoringSelectors';

export type OwnProps = {
    visible: boolean;
    deviceIndex: DeviceIndex;
    onAccept: (base64Image: string, utcTime: UtcTime) => void;
    onCancel: () => void;
};

const styles: { [name: string]: CSSProperties } = {
    buttonBar: {
        position: 'absolute',
        bottom: '1.5em',
        right: '2em',
    },
    cellHost: {
        position: 'absolute',
        top: '5em',
        left: '2em',
        right: '2em',
        bottom: '5em',
    },
    busy: {
        cursor: 'wait !important',
    },
};

export const RawChangeImageForm = ({ visible, deviceIndex, busy, handleKeyDown, handleAccept, onCancel, t }: Props) =>
    <div
        id="change-image-form"
        onKeyDownCapture={ handleKeyDown }
        style={ busy ? styles.busy : undefined }
    >
        <h2>
            { t('2263') }
        </h2>
        <div style={ styles.cellHost }>
            <ChangeImageCell
                busy={ busy }
                visible={ visible }
                deviceIndex={ deviceIndex }
            />
        </div>
        <div style={ styles.buttonBar }>
            <ButtonWithSpinner
                id="change-image-ok-button"
                type="button"
                onClick={ handleAccept }
                textKey="1"
                spinning={ busy }
            />
            <Button
                id="change-image-cancel-button"
                type="button"
                onClick={ onCancel }
                textKey="290"
            />
        </div>
    </div>
;

type StateProps = {
    devices: DeviceMap | null;
    loading: boolean;
    deviceIdByDeviceIndex: DeviceIdMap;
    streamPosition: UtcTime;
};
type DispProps = {
    loadStream: (streamId: string) => void;
    scrollToBottom: () => void;
};
type RecomposeProps = {
    busy: boolean;
    handleAccept: () => void;
    handleKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
    handleLoadCamera: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _setBusy: (b: boolean) => void;
};

const changeImageCellIndex: HasCellIndex = { cellIndex: 0 };

const mapStateToProps = (state: State) => ({
    devices: getDevices(state),
    loading: isLoadingDevices(state),
    deviceIdByDeviceIndex: getDeviceIdByDeviceIndex(state),
    streamPosition: getStreamPosition(state, changeImageCellIndex),
});

const mapDispToProps = (dispatch: Function) => ({
    loadStream: (streamId: string) => dispatch(assignStream({ streamId, cellIndex: 0, paused: true })),
    scrollToBottom: () => dispatch(setScroll({ cellIndex: 0, scroll: 1 })),
});

const acceptImage = async (props: OwnProps & StateProps & DispProps & _RecomposeProps) => {
    const { deviceIndex, deviceIdByDeviceIndex, streamPosition, onAccept, _setBusy } = props;

    _setBusy(true);
    let frame: string;
    let utcTime: UtcTime;
    try {
        const deviceId = deviceIdByDeviceIndex[`${deviceIndex}`];
        const result = await postSeekRequest(deviceId, 0, 'to', streamPosition, false);
        frame = result.frame;
        utcTime = result.utcTime;
    }
    catch (err) {
        // TODO: better handling of the error
        console.error(err);
        return;
    }
    finally {
        _setBusy(false);
    }
    onAccept(frame!, utcTime!);
};

type Props = OwnProps & StateProps & DispProps & RecomposeProps & TranslateProps;

export const ChangeImageForm = compose<Props, OwnProps>(
    setDisplayName('ChangeImageForm'),
    skin,
    translate(),
    connect<StateProps, DispProps, OwnProps>(mapStateToProps, mapDispToProps),
    withState('busy', '_setBusy', false),
    withHandlers<OwnProps & StateProps & DispProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleAccept: props => () => acceptImage(props),
        handleKeyDown: ({ visible, onCancel }) => (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (visible && e.key === 'Escape') {
                onCancel();
            }
        },
    }),
)(RawChangeImageForm);



// WEBPACK FOOTER //
// ./src/ui/pages/changeImage/ChangeImageForm.tsx